// The form controls the Cars screens share.
//
// Same primitives the rest of the panel uses — the browser's native
// <select>/<datalist> can't be styled and looks nothing like everything else,
// so these do the job properly: matching rounded borders, violet focus ring
// and shadowed popover.
//
// CarPicker is the one that matters. It is the SAME "type a few letters and
// pick the car" flow the sales team uses on an inquiry, reading the SAME
// brand/model catalog — so a Swift is entered here exactly as it is entered
// there, and a car nobody has heard of can still be typed in by hand.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiChevronDown, FiEdit3, FiSearch, FiX } from 'react-icons/fi';

export const inputCls =
  'w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300';

// An input with a search icon sitting inside it. Its own class rather than
// `${inputCls} pl-9`: that stacks pl-9 on top of inputCls's px-3, and which one
// wins is down to the order Tailwind happens to emit them in — fine today,
// silently broken the day that changes. The left padding is stated once here.
export const searchInputCls =
  'w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300';

// The icon itself. Centred on whatever `relative` box wraps it — so that box
// must contain the input and NOTHING ELSE, or the icon centres on the
// combined height and slides off the input.
export const searchIconCls =
  'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400';

const menuCls =
  'absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-100 bg-white py-1 shadow-xl';

export const VARIANTS = [
  { value: 'pmt', label: 'Petrol Manual' },
  { value: 'pat', label: 'Petrol Automatic' },
  { value: 'dmt', label: 'Diesel Manual' },
  { value: 'dat', label: 'Diesel Automatic' },
  { value: 'ev', label: 'Electric (EV)' },
];

/** ₹ with Indian grouping. Blank/'—' for nothing, so an empty cell reads empty. */
export const fmtMoney = (v) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—';
};

/** Short money for tiles — ₹5.4 L, ₹1.2 Cr. */
export const fmtShort = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a >= 10000000) return `${sign}₹${(a / 10000000).toFixed(2)} Cr`;
  if (a >= 100000) return `${sign}₹${(a / 100000).toFixed(2)} L`;
  return `${sign}₹${a.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

/** Pull the first useful line out of a DRF error body. */
export const errText = (e, fallback) => {
  const data = e?.response?.data;
  if (typeof data?.error === 'string') return data.error;
  if (data && typeof data === 'object') {
    const first = Object.values(data)[0];
    if (Array.isArray(first) && first.length) return String(first[0]);
    if (typeof first === 'string') return first;
  }
  return e?.message || fallback;
};

/** Close me when a click lands outside, or Escape is pressed. */
const useDismiss = (open, close) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
};

export const Field = ({ label, hint, required, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="mb-1.5 block text-sm font-semibold text-slate-700">
      {label}
      {required && <span className="ml-0.5 text-rose-500">*</span>}
    </span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
  </label>
);

/** Money / quantity input.
 *
 * Deliberately NOT type="number": a native number input silently changes value
 * when the wheel scrolls over it or an arrow key is pressed, so a typed 450000
 * can become 449998 without anyone touching it. This takes digits (and one
 * decimal point) as plain text — what you type is what gets saved.
 */
export const NumberInput = ({ value, onChange, disabled, placeholder, decimals = true, className = '' }) => (
  <input
    type="text"
    inputMode={decimals ? 'decimal' : 'numeric'}
    value={value ?? ''}
    disabled={disabled}
    placeholder={placeholder}
    onChange={(e) => {
      let v = e.target.value.replace(decimals ? /[^0-9.]/g : /[^0-9]/g, '');
      if (decimals) {
        const bits = v.split('.');
        if (bits.length > 2) v = `${bits[0]}.${bits.slice(1).join('')}`;
      }
      onChange(v);
    }}
    className={className || inputCls}
  />
);

/** Styled replacement for <select> — options: [{value, label, hint}] */
export const Select = ({
  value, options, onChange, disabled, placeholder = 'Select…', className = '',
}) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const current = options.find((o) => o.value === value);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between gap-2 rounded-xl border bg-white px-3 py-2 text-left text-sm transition disabled:opacity-60 ${
          open ? 'border-violet-400 ring-2 ring-violet-200' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <span className={current ? 'text-slate-900' : 'text-slate-400'}>
          {current ? current.label : placeholder}
        </span>
        <FiChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={menuCls} role="listbox">
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => { onChange(o.value); close(); }}
                className={`flex w-full items-start justify-between gap-2 px-3.5 py-2 text-left text-sm transition ${
                  active ? 'bg-violet-50 text-violet-700' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className="min-w-0">
                  <span className="block font-semibold">{o.label}</span>
                  {o.hint && <span className="block text-[11px] font-medium text-slate-400">{o.hint}</span>}
                </span>
                {active && <FiCheck className="mt-1 h-3.5 w-3.5 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/** Type-or-pick combo — free text with a styled suggestion list. */
export const ComboInput = ({ value, options, onChange, disabled, placeholder, numeric = false }) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);

  const shown = useMemo(() => {
    const q = String(value || '').trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o).toLowerCase().startsWith(q));
  }, [value, options]);

  return (
    <div className="relative" ref={ref}>
      <div
        className={`flex items-center rounded-xl border bg-white transition ${
          open ? 'border-violet-400 ring-2 ring-violet-200' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <input
          type="text"
          inputMode={numeric ? 'numeric' : 'text'}
          value={value ?? ''}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            const v = numeric ? e.target.value.replace(/[^0-9]/g, '') : e.target.value;
            onChange(v);
            setOpen(true);
          }}
          placeholder={placeholder}
          className="w-full rounded-xl bg-transparent px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 disabled:opacity-60"
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className="px-2.5 text-slate-400"
          aria-label="Open list"
        >
          <FiChevronDown className={`h-4 w-4 transition ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && shown.length > 0 && (
        <div className={menuCls} role="listbox">
          {shown.slice(0, 40).map((o) => (
            <button
              key={o}
              type="button"
              role="option"
              aria-selected={String(o) === String(value)}
              onClick={() => { onChange(String(o)); close(); }}
              className="flex w-full items-center justify-between gap-2 px-3.5 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── The car picker ───────────────────────────────────────────────────────────
// One box over the whole catalogue: type "swi" and pick "Swift · Maruti
// Suzuki". Picking fills brand AND model in one go, which is the point — two
// separate dropdowns means picking a brand, waiting, then hunting a model.
//
// A car needs a brand and a model, so unlike the parts-stock picker there is no
// "fits any car" state. What there IS, is a way out: "Not in the list?" opens
// two plain boxes, because the catalogue will never have every car that rolls
// onto the lot, and being unable to type one in would stop the work.
export const CarPicker = ({ catalog = [], brand, model, onPick, disabled }) => {
  const [query, setQuery] = useState('');
  const [manual, setManual] = useState(false);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out = [];
    for (const b of catalog) {
      const brandHit = b.name.toLowerCase().includes(q);
      for (const m of b.models || []) {
        if (brandHit || m.name.toLowerCase().includes(q)) {
          out.push({ brand: b.name, model: m.name });
        }
      }
    }
    return out.slice(0, 10);
  }, [catalog, query]);

  // Already picked — show the car as a chip that can be cleared.
  if (brand && model && !manual) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-bold text-violet-800">
          {brand} {model}
          <button
            type="button"
            disabled={disabled}
            onClick={() => { onPick('', ''); setQuery(''); }}
            className="rounded-full p-0.5 text-violet-400 transition hover:bg-white hover:text-rose-600"
            aria-label="Change car"
          >
            <FiX className="h-3.5 w-3.5" />
          </button>
        </span>
      </div>
    );
  }

  if (manual) {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <input
            type="text"
            value={brand || ''}
            disabled={disabled}
            onChange={(e) => onPick(e.target.value, model || '')}
            placeholder="Brand — e.g. Force"
            className={inputCls}
          />
          <input
            type="text"
            value={model || ''}
            disabled={disabled}
            onChange={(e) => onPick(brand || '', e.target.value)}
            placeholder="Model — e.g. Gurkha"
            className={inputCls}
          />
        </div>
        <button
          type="button"
          onClick={() => { setManual(false); onPick('', ''); }}
          className="text-xs font-bold text-violet-600 hover:text-violet-700"
        >
          ← Back to search
        </button>
        <p className="text-[11px] text-slate-400">
          A car typed here is added to the shared catalogue, so next time it is one search away.
        </p>
      </div>
    );
  }

  return (
    // Two boxes, not one. The inner `relative` holds ONLY the input and the
    // things anchored to it (the icon, the dropdown), so `top-1/2` means the
    // middle of the input. The helper link below sits outside it — inside, its
    // height would drag the lens down off the input.
    <div ref={ref}>
      <div className="relative">
        <FiSearch className={searchIconCls} />
        <input
          type="text"
          value={query}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          placeholder="Search the car — Swift, Creta, Seltos…"
          className={searchInputCls}
        />
        {open && query.trim().length > 0 && (
          <div className={menuCls} role="listbox">
            {matches.map((m) => (
              <button
                key={`${m.brand}-${m.model}`}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => { onPick(m.brand, m.model); setQuery(''); close(); }}
                className="flex w-full items-baseline gap-2 px-3.5 py-2 text-left text-sm transition hover:bg-slate-50"
              >
                <span className="font-bold text-slate-800">{m.model}</span>
                <span className="text-xs font-semibold text-slate-400">{m.brand}</span>
              </button>
            ))}
            {matches.length === 0 && (
              <p className="px-3.5 py-2 text-sm text-slate-400">
                Nothing in the catalogue matches “{query.trim()}”.
              </p>
            )}
            <button
              type="button"
              onClick={() => { setManual(true); onPick(query.trim(), ''); close(); }}
              className="mt-1 flex w-full items-center gap-2 border-t border-slate-100 px-3.5 py-2 text-left text-sm font-bold text-violet-600 transition hover:bg-violet-50"
            >
              <FiEdit3 className="h-3.5 w-3.5" />
              Not in the list — type it in
            </button>
          </div>
        )}
      </div>

      {!open && (
        <button
          type="button"
          onClick={() => setManual(true)}
          className="mt-1.5 text-[11px] font-bold text-violet-600 hover:text-violet-700"
        >
          Not in the list? Type it in
        </button>
      )}
    </div>
  );
};

/** A headline number with its own colour. */
export const StatCard = ({ icon: Icon, label, value, tone = 'slate', hint }) => {
  const tones = {
    violet: 'bg-violet-50 text-violet-600',
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600',
    sky: 'bg-sky-50 text-sky-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    slate: 'bg-slate-100 text-slate-500',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="truncate text-lg font-bold text-slate-900">{value}</p>
          {hint && <p className="truncate text-[11px] text-slate-400">{hint}</p>}
        </div>
      </div>
    </div>
  );
};

/** A modal that behaves — click the backdrop or press Escape to leave. */
export const Modal = ({ open, onClose, title, subtitle, children, wide }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm sm:p-8">
      <div
        className="absolute inset-0"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className={`relative w-full ${wide ? 'max-w-3xl' : 'max-w-xl'} rounded-2xl bg-white shadow-2xl`}>
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-black text-slate-900">{title}</h2>
            {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
};
